import first from "lodash/first";
import { useRouter } from "next/router";
import qs from "qs";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { isString } from "~/lib/utils";
import type { hooksNewsletterScrollEffectNewsletterCategoryConnection$key } from "~/queries/__generated__/hooksNewsletterScrollEffectNewsletterCategoryConnection.graphql";
import type { hooksPreselectNewsletterNewsletterCategoryConnection$key } from "~/queries/__generated__/hooksPreselectNewsletterNewsletterCategoryConnection.graphql";

import type { DefaultCheckedQueryStringPayload } from "./types";

export const useNewsletterScrollEffect = (
  newsletterCategory_: hooksNewsletterScrollEffectNewsletterCategoryConnection$key | null,
) => {
  const newsletterCategory = useFragment(
    graphql`
      fragment hooksNewsletterScrollEffectNewsletterCategoryConnection on NewsletterCategoryConnection {
        edges {
          node {
            entityId
          }
        }
      }
    `,
    newsletterCategory_,
  );

  const [activeNewsletterCategoryId, setActiveNewsletterCategoryId] = useState(
    first(newsletterCategory?.edges)?.node?.entityId ?? "",
  );
  const [isStopIntersectionObserve, setIsStopIntersectionObserve] = useState(false);
  const [needToScrollId, setNeedToScrollId] = useState<string>();
  const handleCategoryItemListIntersection = useCallback((newsletterCategoryId: string) => {
    setActiveNewsletterCategoryId(newsletterCategoryId);
  }, []);

  const handleCategoryClick = useCallback((entityId: string) => {
    setIsStopIntersectionObserve(true);
    setNeedToScrollId(entityId);
  }, []);

  const newsletterCategoryItemListReferences = useRef({} as Record<string, HTMLDivElement>);
  useEffect(() => {
    if (!needToScrollId) return;
    const elementPosition =
      newsletterCategoryItemListReferences.current[needToScrollId].getBoundingClientRect().top;
    document.scrollingElement?.scroll({
      behavior: "smooth",
      top: document.scrollingElement.scrollTop + elementPosition - 63, // header bar offset
    });
    setActiveNewsletterCategoryId(needToScrollId);
    setNeedToScrollId(undefined);
    // Delay the stop observe for waiting the scroll effect
    setTimeout(() => setIsStopIntersectionObserve(false), 1000);
  }, [needToScrollId]);

  return {
    activeNewsletterCategoryId,
    handleCategoryClick,
    handleCategoryItemListIntersection,
    isStopIntersectionObserve,
    newsletterCategoryItemListReferences,
  };
};

export const usePreselectNewsletter = (
  newsletterCategory_: hooksPreselectNewsletterNewsletterCategoryConnection$key | null,
  setCheckedNewsletterIdsMap: Dispatch<SetStateAction<Record<string, string[]>>>,
  setActiveNewsletterId: Dispatch<SetStateAction<string>>,
) => {
  const newsletterCategory = useFragment(
    graphql`
      fragment hooksPreselectNewsletterNewsletterCategoryConnection on NewsletterCategoryConnection {
        edges {
          node {
            entityId
            newsletters(first: 100, orderBy: { field: WEIGHT, direction: DESC }) {
              edges {
                node {
                  entityId
                }
              }
            }
          }
        }
      }
    `,
    newsletterCategory_,
  );

  const newsletterCategoryIdMap = useMemo(
    () =>
      newsletterCategory?.edges.flatMap(category =>
        category.node.newsletters?.edges.map(newsletter => ({
          categoryId: category.node.entityId,
          entityId: newsletter.node.entityId,
        })),
      ),
    [newsletterCategory],
  );

  const { pathname, query } = useRouter();
  useEffect(() => {
    const parsedQueryString = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    }) as unknown as DefaultCheckedQueryStringPayload;
    // If query already has entity, use it instead (eg: /newsletter/style-invite)
    // or use the query string afterward (eg: /newsletter?defaultCheckedEntityIds=324615,324616)
    let defaultCheckedEntityIds = parsedQueryString.defaultCheckedEntityIds?.split?.(",");
    if (query.entityId && isString(query.entityId)) {
      defaultCheckedEntityIds = [query.entityId];
      setActiveNewsletterId(query.entityId);
    }

    if (!defaultCheckedEntityIds || !newsletterCategoryIdMap) return;
    setCheckedNewsletterIdsMap(
      defaultCheckedEntityIds.reduce((accumulator, entityId) => {
        const newsletterInMap = newsletterCategoryIdMap.find(
          newsletter => newsletter?.entityId === entityId,
        );
        if (!newsletterInMap) return accumulator;
        accumulator[newsletterInMap.categoryId] = [
          ...(accumulator[newsletterInMap.categoryId] ?? []),
          newsletterInMap.entityId,
        ];
        return accumulator;
      }, {} as Record<string, string[]>),
    );
  }, [pathname, newsletterCategoryIdMap, setCheckedNewsletterIdsMap, query, setActiveNewsletterId]);
};
